import type { ReactNode } from "react";

import AttendanceSection from "./sections/AttendanceSection";
import WorkLogsSection from "./sections/WorkLogsSection";
import HoursApprovalSection from "./sections/HoursApprovalSection";
import AdjustmentsSection from "./sections/AdjustmentsSection";
import PayslipsSection from "./sections/PayslipsSection";
import ContributionSection from "./sections/ContributionSection";
import TimeClockSection from "./sections/TimeClockSection";
import PaymentsSection from "./sections/PaymentsSection";
import PaidPeriodsSection from "./sections/PaidPeriodsSection";

// ---------------------------------------------------------------------------
// Fuente única de navegación de la mini-app "Asistencia, Anticipos y Nómina"
// (issues AgroCloud#36/#37/#38/#81/#82/#83). Alimenta:
//   - el sub-menú por sección (PayrollLayout)
//   - las rutas anidadas (router/config.tsx vía routes.tsx)
//   - el tile del apps-sidebar (AppsSidebar/appsConfig.tsx)
// Las etiquetas son claves i18n resueltas al render en el layout.
//
// El orden de las secciones sigue el flujo real del dinero, no el número de
// las issues: primero se captura (asistencia, incidencias, anticipos), luego se
// autoriza, y solo al final se liquida y se timbra.
//   - "asistencia"  (AgroCloud#36) — jornadas y ausencias por colaborador y fecha.
//   - "horas por aprobar" (#1346) — cada quien registra sus horas; tech leads y dueños aprueban.
//   - "incidencias" (AgroCloud#81) — captura del equipo + autorización del supervisor.
//   - "movimientos" (AgroCloud#37) — anticipos, préstamos y otros haberes/descuentos.
//   - "periodos"    (AgroCloud#82) — periodos de nómina, tipos y cierre.
//   - "liquidaciones" (AgroCloud#38 + AgroCloud#82) — liquidación de sueldo y su CFDI.
//   - "previsional" (AgroCloud#38) — parámetros/entidades previsionales.
//   - "reloj-control" (AgroCloud#83) — biometría/reloj checador: MAQUETA (coming-soon).
//
// ⚠️ El id del tile (`payslip`) resuelve los textos (`apps.payroll.*` en
// i18n/locales/common) y la visibilidad por tenant (config/apps/*.json). Si
// cambias el id, cambia los tres a la vez: una app ausente del preset del tenant
// se filtra y desaparece del Apps Hub.
// ---------------------------------------------------------------------------

export interface PayrollNavItem {
  id: string; // segmento de ruta bajo /payslip
  labelKey: string; // clave i18n del namespace common
  icon: string; // clase remixicon del sub-menú
  element: ReactNode;
  /**
   * Secciones con dinero (#742): sólo se le ofrecen a quien administra. No es
   * el control de acceso —ése vive en el servidor, que responde 403— sino no
   * ofrecer una pantalla que llegaría vacía o con un error.
   */
  adminOnly?: boolean;
  /**
   * Solo dueños (#1027): rol owner + acceso a Finanzas por correo. Nómina pagada
   * contra estados de cuenta. El servidor responde 403 a cualquiera más.
   */
  ownerOnly?: boolean;
}

export const MENU: PayrollNavItem[] = [
  {
    // #1025: primero, porque es lo que cada colaborador viene a buscar: sus
    // nóminas pagadas y el comprobante de cada pago.
    id: "payments",
    labelKey: "payroll.menu.payments",
    icon: "ri-wallet-3-line",
    element: <PaymentsSection />,
  },
  {
    id: "attendance",
    labelKey: "payroll.menu.attendance",
    icon: "ri-calendar-check-line",
    element: <AttendanceSection />,
  },
  {
    // #1346: sección propia (antes era una tabla encima de las incidencias).
    // La ven TODOS: aquí cada colaborador registra sus horas y sigue sus
    // solicitudes; los tech leads y dueños las aprueban o rechazan.
    id: "hours-approval",
    labelKey: "payroll.menu.hoursApproval",
    icon: "ri-hourglass-line",
    element: <HoursApprovalSection />,
  },
  {
    // #1346: además de las incidencias, la tabla de horas por colaborador
    // (justificadas, por aprobar, pérdida o ganancia, tiempo por repo).
    id: "work-logs",
    labelKey: "payroll.menu.workLogs",
    icon: "ri-shield-check-line",
    element: <WorkLogsSection />,
  },
  {
    id: "adjustments",
    labelKey: "payroll.menu.adjustments",
    icon: "ri-hand-coin-line",
    element: <AdjustmentsSection />,
  },
  {
    id: "payslips",
    labelKey: "payroll.menu.payslips",
    icon: "ri-file-list-3-line",
    element: <PayslipsSection />,
  },
  {
    // #1141: «Periodos de nómina» e «Historial pagado» son una sola sección:
    // periodos pagados (todos, solo lo suyo), conciliación con estados de
    // cuenta (dueños) y catálogo CFDI. /payroll/periods redirige aquí.
    id: "paid-history",
    labelKey: "payroll.menu.paidHistory",
    icon: "ri-calendar-check-line",
    element: <PaidPeriodsSection />,
  },
  {
    id: "contributions",
    labelKey: "payroll.menu.contribution",
    icon: "ri-shield-user-line",
    element: <ContributionSection />,
  },
  {
    id: "time-clock",
    labelKey: "payroll.menu.timeClock",
    icon: "ri-fingerprint-line",
    element: <TimeClockSection />,
  },
];

export const PAYROLL_BASE = "/payroll";

/** Ruta de aterrizaje por defecto («Mis pagos», #1025). */
export const payrollDefaultRoute = `${PAYROLL_BASE}/${MENU[0].id}`;

/** Metadatos del tile del apps-sidebar. */
export const payrollApp = {
  id: "payroll",
  iconClass: "ri-money-dollar-circle-line",
  color: "bg-emerald-600",
  route: payrollDefaultRoute,
};
